import { useState, useEffect, useCallback, useMemo } from 'react';
import { backdropClose } from '../../utils/backdropClose';
import { ChevronLeft, ChevronRight, AlertTriangle, Image as ImageIcon, X } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useUserContext } from '../../contexts/UserContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AttendanceLog {
  id: string;
  teamMember: { id: string; name: string; displayName?: string; role: string };
  checkInTime: string;
  checkOutTime?: string;
  imageUrl?: string | null;
  checkOutImageUrl?: string | null;
}

interface LeaveRecord {
  id: string;
  teamMember: { id: string; name: string; displayName?: string; role: string };
  startDate: string;
  endDate: string;
  leaveType: string;
  isHalfDay: boolean;
  halfDayType?: string;
  status: 'pending' | 'approved' | 'rejected' | 'deleted';
}

interface TeamMemberInfo {
  id: string;
  name: string;
  displayName?: string;
  role: string;
}

interface TeamAttendanceTabProps {
  branches: Array<{ id: string; name: string; displayName: string }>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RETENTION_DAYS = 7;

const ROLE_COLORS: Record<string, string> = {
  admin: 'text-purple-600',
  trainer: 'text-blue-600',
  staff: 'text-gray-500'
};

// ─── Date helpers ────────────────────────────────────────────────────────────

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day); // Monday-based
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isPastDay(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

function isFutureDay(date: Date): boolean {
  return !isPastDay(date) && !isToday(date);
}

function leaveCoversDay(leave: LeaveRecord, day: Date): boolean {
  const start = new Date(leave.startDate);
  const end = new Date(leave.endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return day >= start && day <= end;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatHours(checkIn: string, checkOut: string): string {
  const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatDayHeader(date: Date): { label: string; sub: string; iso: string } {
  const label = DAYS[date.getDay() === 0 ? 6 : date.getDay() - 1];
  const sub = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const iso = date.toISOString().split('T')[0];
  return { label, sub, iso };
}

function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── Image lightbox ──────────────────────────────────────────────────────────

function ImageLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      {...backdropClose(onClose)}
    >
      <img
        src={url}
        alt="Selfie"
        className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

// ─── Cell component ──────────────────────────────────────────────────────────

interface AttendanceCellProps {
  day: Date;
  log?: AttendanceLog;
  leaves: LeaveRecord[];
  onImageClick: (url: string) => void;
  onManualClick?: () => void;
  isOwner: boolean;
}

function AttendanceCell({ day, log, leaves, onImageClick, onManualClick, isOwner }: AttendanceCellProps) {
  const now = new Date();
  const past = isPastDay(day);
  const future = isFutureDay(day);
  const within7Days = (now.getTime() - day.getTime()) < RETENTION_DAYS * 86_400_000;

  const activeLeaves = leaves.filter(l =>
    leaveCoversDay(l, day) && l.status !== 'rejected' && l.status !== 'deleted'
  );

  const approvedLeave = activeLeaves.find(l => l.status === 'approved');
  const pendingLeave = activeLeaves.find(l => l.status === 'pending');
  const anyLeave = approvedLeave || pendingLeave;

  const hasCheckIn = !!log?.checkInTime;
  const hasCheckOut = !!log?.checkOutTime;
  const missingCheckout = hasCheckIn && !hasCheckOut && past;

  // Future: only leaves shown
  if (future && !anyLeave) {
    return <div className="h-full min-h-[80px] bg-gray-50/50 rounded-lg" />;
  }

  const todayCell = isToday(day);

  return (
    <div className={`min-h-[80px] rounded-lg p-2 flex flex-col gap-1 text-xs border transition-colors ${
      todayCell ? 'border-blue-200 bg-blue-50/50' : 'border-transparent bg-white'
    }`}>
      {/* Attendance block */}
      {hasCheckIn && (
        <div className={`rounded-md px-2 py-1.5 flex flex-col gap-0.5 ${
          hasCheckOut ? 'bg-green-50 border border-green-200' :
          missingCheckout ? 'bg-amber-50 border border-amber-300' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center justify-between gap-1">
            <span className="font-semibold text-green-700">IN {formatTime(log!.checkInTime)}</span>
            {log!.imageUrl && within7Days && (
              <button
                onClick={() => onImageClick(log!.imageUrl!)}
                className="text-green-500 hover:text-green-700 flex-shrink-0"
                title="View check-in selfie"
              >
                <ImageIcon size={11} />
              </button>
            )}
          </div>
          {hasCheckOut && (
            <div className="flex items-center justify-between gap-1">
              <span className="font-semibold text-orange-600">OUT {formatTime(log!.checkOutTime!)}</span>
              {log!.checkOutImageUrl && within7Days && (
                <button
                  onClick={() => onImageClick(log!.checkOutImageUrl!)}
                  className="text-orange-400 hover:text-orange-600 flex-shrink-0"
                  title="View check-out selfie"
                >
                  <ImageIcon size={11} />
                </button>
              )}
            </div>
          )}
          {hasCheckOut && (
            <span className="text-gray-500 text-[10px]">{formatHours(log!.checkInTime, log!.checkOutTime!)}</span>
          )}
          {missingCheckout && (
            <div className="flex items-center gap-1 text-amber-600 mt-0.5">
              <AlertTriangle size={10} />
              <span className="text-[10px]">Missing checkout</span>
            </div>
          )}
        </div>
      )}

      {/* Leave block */}
      {anyLeave && (
        <div className={`rounded-md px-2 py-1.5 flex flex-col gap-0.5 ${
          approvedLeave
            ? 'bg-blue-50 border border-blue-200'
            : 'bg-yellow-50 border border-yellow-300'
        }`}>
          <span className={`font-medium capitalize truncate ${approvedLeave ? 'text-blue-700' : 'text-yellow-700'}`}>
            {(approvedLeave || pendingLeave)!.leaveType.replace('_', ' ')}
            {(approvedLeave || pendingLeave)!.isHalfDay && ' (½)'}
          </span>
          {pendingLeave && (
            <span className="text-yellow-600 text-[10px]">Pending approval</span>
          )}
        </div>
      )}

      {/* Empty past day indicator */}
      {!hasCheckIn && !anyLeave && past && (
        <div className="flex-1 flex items-center justify-center group/cell">
          {isOwner ? (
            <button
              onClick={onManualClick}
              className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 hover:bg-[#AAD816]/20 hover:text-black flex items-center justify-center transition-all opacity-0 group-hover/cell:opacity-100"
              title="Record manual attendance"
            >
              <span className="text-lg font-bold">+</span>
            </button>
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-gray-200" />
          )}
        </div>
      )}

      {/* Partial check-in indicator (missing checkout) - allow editing if owner */}
      {missingCheckout && isOwner && (
        <button
          onClick={onManualClick}
          className="mt-1 w-full text-[9px] font-black uppercase text-blue-600 hover:underline text-left"
        >
          Update Record
        </button>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function TeamAttendanceTab({ branches: _branches }: TeamAttendanceTabProps) {
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [attendanceLogs, setAttendanceLogs] = useState<AttendanceLog[]>([]);
  const [leaveRecords, setLeaveRecords] = useState<LeaveRecord[]>([]);
  const [allMembers, setAllMembers] = useState<TeamMemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Manual record state
  const [manualRecord, setManualRecord] = useState<{ member: TeamMemberInfo; day: Date; log?: AttendanceLog } | null>(null);
  const [manualTimes, setManualTimes] = useState({ checkIn: '09:00', checkOut: '18:00', hasCheckOut: true });
  const [isSavingManual, setIsSavingManual] = useState(false);

  const { hasRole } = useUserContext();
  const isOwner = hasRole('owner');

  const today = new Date();
  const minWeekStart = getWeekStart(addDays(today, -70)); // 10 weeks back
  const maxWeekStart = getWeekStart(addDays(today, 60));  // ~2 months forward

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = weekDays[6];

  const canGoPrev = weekStart > minWeekStart;
  const canGoNext = weekStart < maxWeekStart;

  const goToWeek = (offset: number) => {
    setWeekStart(prev => {
      const next = addDays(prev, offset * 7);
      if (offset < 0 && next < minWeekStart) return minWeekStart;
      if (offset > 0 && next > maxWeekStart) return maxWeekStart;
      return next;
    });
  };

  const resetToCurrentWeek = () => setWeekStart(getWeekStart(new Date()));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const headers = { 'Authorization': `Bearer ${session.access_token}` };
      const startStr = toLocalDateString(weekStart);
      const endStr = toLocalDateString(weekEnd);

      const [attRes, leavesRes, membersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/team-attendance?startDate=${startStr}&endDate=${endStr}`, { headers }),
        fetch(`${API_BASE_URL}/api/leaves?startDate=${startStr}&endDate=${endStr}`, { headers }),
        fetch(`${API_BASE_URL}/api/team-attendance/team-members`, { headers })
      ]);

      if (attRes.ok) {
        const d = await attRes.json();
        setAttendanceLogs(d.data || []);
      }
      if (leavesRes.ok) {
        const d = await leavesRes.json();
        setLeaveRecords(d.data || []);
      }
      if (membersRes.ok) {
        const d = await membersRes.json();
        // Map team-members response (userId, name, displayName, role) to TeamMemberInfo (id = userId)
        setAllMembers((d.data || []).map((m: any) => ({
          id: m.userId,
          name: m.name,
          displayName: m.displayName,
          role: m.role
        })));
      }
    } catch {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleManualRecord = async () => {
    if (!manualRecord) return;
    setIsSavingManual(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const dateStr = toLocalDateString(manualRecord.day);
      const checkInTime = new Date(`${dateStr}T${manualTimes.checkIn}:00`);
      const checkOutTime = manualTimes.hasCheckOut 
        ? new Date(`${dateStr}T${manualTimes.checkOut}:00`)
        : null;

      const res = await fetch(`${API_BASE_URL}/api/team-attendance/manual`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teamMemberId: manualRecord.member.id,
          date: dateStr,
          checkInTime: checkInTime.toISOString(),
          checkOutTime: checkOutTime?.toISOString() || null
        })
      });

      if (res.ok) {
        toast.success('Attendance recorded');
        setManualRecord(null);
        fetchData();
      } else {
        const d = await res.json();
        toast.error(d.error || 'Failed to record');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setIsSavingManual(false);
    }
  };

  const openManualModal = (member: TeamMemberInfo, day: Date, log?: AttendanceLog) => {
    setManualRecord({ member, day, log });
    if (log) {
      const cin = new Date(log.checkInTime);
      setManualTimes({
        checkIn: `${String(cin.getHours()).padStart(2, '0')}:${String(cin.getMinutes()).padStart(2, '0')}`,
        checkOut: log.checkOutTime 
          ? `${String(new Date(log.checkOutTime).getHours()).padStart(2, '0')}:${String(new Date(log.checkOutTime).getMinutes()).padStart(2, '0')}`
          : '18:00',
        hasCheckOut: !!log.checkOutTime
      });
    } else {
      setManualTimes({ checkIn: '09:00', checkOut: '18:00', hasCheckOut: true });
    }
  };

  // Build member list: start from all team members (so every staff/trainer/admin row appears),
  // then merge in anyone who has a log/leave but isn't in the team-members list (edge case).
  const memberMap = new Map<string, TeamMemberInfo>();

  allMembers.forEach(m => memberMap.set(m.id, m));

  attendanceLogs.forEach(log => {
    if (!memberMap.has(log.teamMember.id)) {
      memberMap.set(log.teamMember.id, log.teamMember);
    }
  });
  leaveRecords.forEach(leave => {
    if (!memberMap.has(leave.teamMember.id)) {
      memberMap.set(leave.teamMember.id, leave.teamMember);
    }
  });

  const members = Array.from(memberMap.values()).sort((a, b) =>
    (a.displayName || a.name).localeCompare(b.displayName || b.name)
  );

  // Look up log for a member on a specific day
  function getLog(memberId: string, day: Date): AttendanceLog | undefined {
    return attendanceLogs.find(log =>
      log.teamMember.id === memberId && isSameDay(new Date(log.checkInTime), day)
    );
  }

  // Look up leaves for a member on a specific day
  function getMemberLeaves(memberId: string, day: Date): LeaveRecord[] {
    return leaveRecords.filter(l =>
      l.teamMember.id === memberId && leaveCoversDay(l, day)
    );
  }

  const weekLabel = (() => {
    const s = weekStart;
    const e = weekEnd;
    const sStr = s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    const eStr = e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${sStr} – ${eStr}`;
  })();

  const isCurrentWeek = isSameDay(weekStart, getWeekStart(new Date()));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Week navigation header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between gap-4">
        <button
          onClick={() => goToWeek(-1)}
          disabled={!canGoPrev}
          className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex flex-col items-center gap-1">
          <span className="text-sm font-semibold text-gray-900">{weekLabel}</span>
          {!isCurrentWeek && (
            <button
              onClick={resetToCurrentWeek}
              className="text-xs text-blue-600 hover:underline font-medium"
            >
              This Week
            </button>
          )}
        </div>

        <button
          onClick={() => goToWeek(1)}
          disabled={!canGoNext}
          className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Calendar grid */}
      {members.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500 text-sm">No attendance records or leaves for this week.</p>
          <p className="text-gray-400 text-xs mt-1">Check-in and leaves will appear here once logged.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '140px', minWidth: '120px' }} />
              {weekDays.map((_, i) => (
                <col key={i} style={{ width: '120px', minWidth: '110px' }} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tl-xl">
                  Staff
                </th>
                {weekDays.map((day, i) => {
                  const { label, sub } = formatDayHeader(day);
                  const todayCell = isToday(day);
                  return (
                    <th
                      key={i}
                      className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${
                        todayCell
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-50 text-gray-500'
                      } ${i === 6 ? 'rounded-tr-xl' : ''}`}
                    >
                      <div>{label}</div>
                      <div className={`text-[11px] font-medium normal-case mt-0.5 ${todayCell ? 'text-blue-500' : 'text-gray-400'}`}>
                        {sub}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((member, rowIdx) => (
                <tr key={member.id} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                  {/* Staff name cell */}
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {(member.displayName || member.name)[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-900 truncate">
                          {member.displayName || member.name}
                        </div>
                        <div className={`text-[10px] font-medium capitalize ${ROLE_COLORS[member.role] || 'text-gray-400'}`}>
                          {member.role}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Day cells */}
                  {weekDays.map((day, colIdx) => {
                    const log = getLog(member.id, day);
                    const leaves = getMemberLeaves(member.id, day);
                    return (
                      <td key={colIdx} className="px-1 py-1 align-top">
                        <AttendanceCell
                          day={day}
                          log={log}
                          leaves={leaves}
                          onImageClick={url => setLightboxUrl(url)}
                          isOwner={isOwner}
                          onManualClick={() => openManualModal(member, day, log)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
          <span>Checked in & out</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-amber-50 border border-amber-300" />
          <span>Missing checkout</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-blue-50 border border-blue-200" />
          <span>Approved leave</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-50 border border-yellow-300" />
          <span>Pending leave</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ImageIcon size={12} className="text-gray-400" />
          <span>Click to view selfie (7-day retention)</span>
        </div>
      </div>

      {/* Image lightbox */}
      {lightboxUrl && (
        <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}

      {/* Manual Attendance Modal */}
      {manualRecord && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in duration-200">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black text-gray-900 uppercase">Manual Attendance</h3>
                <p className="text-[10px] text-zinc-400 font-bold uppercase mt-0.5">
                  {manualRecord.member.displayName || manualRecord.member.name} · {manualRecord.day.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </p>
              </div>
              <button onClick={() => setManualRecord(null)} className="text-gray-400 hover:text-black">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase">Check In</label>
                  <input
                    type="time"
                    value={manualTimes.checkIn}
                    onChange={e => setManualTimes(prev => ({ ...prev, checkIn: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#AAD816]/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase">Check Out</label>
                  <input
                    type="time"
                    disabled={!manualTimes.hasCheckOut}
                    value={manualTimes.checkOut}
                    onChange={e => setManualTimes(prev => ({ ...prev, checkOut: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#AAD816]/20 disabled:opacity-50"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${manualTimes.hasCheckOut ? 'bg-black border-black' : 'border-zinc-300'}`}>
                  <input
                    type="checkbox"
                    checked={manualTimes.hasCheckOut}
                    onChange={e => setManualTimes(prev => ({ ...prev, hasCheckOut: e.target.checked }))}
                    className="hidden"
                  />
                  {manualTimes.hasCheckOut && <div className="w-1.5 h-1.5 bg-[#AAD816] rounded-full" />}
                </div>
                <span className="text-[10px] font-black text-gray-400 uppercase group-hover:text-gray-600 transition-colors">Has Check Out</span>
              </label>

              <button
                onClick={handleManualRecord}
                disabled={isSavingManual}
                className="w-full py-3 bg-[#AAD816] hover:bg-black hover:text-[#AAD816] text-black font-black uppercase text-xs rounded-xl shadow-lg shadow-[#AAD816]/20 transition-all active:scale-95 disabled:opacity-50"
              >
                {isSavingManual ? 'Saving...' : 'Save Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
